import { useState, useCallback, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Save, Eye, Code, Plus, Trash2, GripVertical, RefreshCw, Download } from 'lucide-react'
import { getPdfTemplate, updatePdfTemplate, previewPdfTemplate, generatePdf } from '../api/storage'
import { Field } from '../components/Field'
import { Modal } from '../components/Modal'
import { Button } from '../components/ui/button'
import { Select, SelectItem } from '../components/ui/select'
import type {
  PdfTemplateConfig, PdfSection,
  PdfHeaderField, PdfTableColumn, PdfSummaryRow, PdfSignatureBlock,
} from '../types'
const uuidv4 = () => crypto.randomUUID()

// ── Helpers ────────────────────────────────────────────────────────────────────

const FONTS = ['Arial, Helvetica, sans-serif', 'Georgia, "Times New Roman", serif', '"Courier New", Courier, monospace', 'Verdana, Geneva, sans-serif', '"Trebuchet MS", sans-serif']
const FONT_LABELS: Record<string, string> = {
  'Arial, Helvetica, sans-serif': 'Arial',
  'Georgia, "Times New Roman", serif': 'Georgia',
  '"Courier New", Courier, monospace': 'Courier New',
  'Verdana, Geneva, sans-serif': 'Verdana',
  '"Trebuchet MS", sans-serif': 'Trebuchet MS',
}
const SECTION_TYPES = ['text', 'table', 'summary', 'signature', 'divider', 'spacer'] as const
const SECTION_TYPE_LABELS: Record<string, string> = { text: 'Text Field', table: 'Table', summary: 'Summary Rows', signature: 'Signature Blocks', divider: 'Divider', spacer: 'Spacer' }

// ── Config panel tabs ─────────────────────────────────────────────────────────

type ConfigTab = 'branding' | 'header' | 'sections' | 'footer' | 'page'

// ── Branding tab ──────────────────────────────────────────────────────────────

function BrandingPanel({ config, onChange }: { config: PdfTemplateConfig; onChange: (c: PdfTemplateConfig) => void }) {
  const set = (key: keyof PdfTemplateConfig, val: any) => onChange({ ...config, [key]: val })
  return (
    <div className="space-y-4">
      <Field label="Primary color">
        <div className="flex items-center gap-2">
          <input type="color" value={config.primaryColor} onChange={e => set('primaryColor', e.target.value)}
            className="w-8 h-8 rounded border border-paper-border cursor-pointer p-0.5" />
          <input value={config.primaryColor} onChange={e => set('primaryColor', e.target.value)}
            className="field flex-1 font-mono text-xs" />
        </div>
      </Field>
      <Field label="Secondary / accent color">
        <div className="flex items-center gap-2">
          <input type="color" value={config.secondaryColor} onChange={e => set('secondaryColor', e.target.value)}
            className="w-8 h-8 rounded border border-paper-border cursor-pointer p-0.5" />
          <input value={config.secondaryColor} onChange={e => set('secondaryColor', e.target.value)}
            className="field flex-1 font-mono text-xs" />
        </div>
      </Field>
      <Field label="Font family">
        <Select value={config.fontFamily} onValueChange={v => set('fontFamily', v)}>
          {FONTS.map(f => <SelectItem key={f} value={f}>{FONT_LABELS[f] ?? f}</SelectItem>)}
        </Select>
      </Field>
      <Field label="Logo URL or base64">
        <input value={config.logo ?? ''} onChange={e => set('logo', e.target.value || undefined)}
          placeholder="https://… or data:image/png;base64,…" className="field text-xs" />
      </Field>
    </div>
  )
}

// ── Header tab ────────────────────────────────────────────────────────────────

function HeaderPanel({ config, onChange }: { config: PdfTemplateConfig; onChange: (c: PdfTemplateConfig) => void }) {
  const h   = config.header
  const set = (key: string, val: any) => onChange({ ...config, header: { ...h, [key]: val } })

  const addField = () => set('fields', [...h.fields, { id: uuidv4(), label: 'Label', variable: 'variable' }])
  const updateField = (idx: number, k: keyof PdfHeaderField, v: string) => {
    const next = [...h.fields]; next[idx] = { ...next[idx], [k]: v }; set('fields', next)
  }
  const removeField = (idx: number) => set('fields', h.fields.filter((_, i) => i !== idx))

  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={h.enabled} onChange={e => set('enabled', e.target.checked)} className="rounded" />
        <span className="text-sm">Show header</span>
      </label>

      {h.enabled && (
        <>
          <Field label="Business name">
            <input value={h.businessName} onChange={e => set('businessName', e.target.value)} className="field" />
          </Field>
          <Field label="Business address">
            <textarea value={h.businessAddress} onChange={e => set('businessAddress', e.target.value)}
              rows={2} className="field resize-none" />
          </Field>
          <Field label="Contact (email / phone)">
            <input value={h.businessContact} onChange={e => set('businessContact', e.target.value)} className="field" />
          </Field>
          <Field label="Document title (e.g. INVOICE)">
            <input value={h.titleText} onChange={e => set('titleText', e.target.value)}
              placeholder="INVOICE" className="field" />
          </Field>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest">Header fields</span>
              <button onClick={addField} className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover">
                <Plus size={12} /> Add field
              </button>
            </div>
            <div className="space-y-2">
              {h.fields.map((f, i) => (
                <div key={f.id} className="flex items-center gap-2">
                  <input value={f.label} onChange={e => updateField(i, 'label', e.target.value)}
                    placeholder="Label" className="field flex-1 text-xs" />
                  <input value={f.variable} onChange={e => updateField(i, 'variable', e.target.value)}
                    placeholder="payloadKey" className="field flex-1 text-xs font-mono" />
                  <button onClick={() => removeField(i)} className="text-ink-dim hover:text-red-500 transition-colors">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              {h.fields.length === 0 && <p className="text-xs text-ink-dim italic">No fields. Add fields like "Invoice #", "Date"…</p>}
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={h.showRecipient} onChange={e => set('showRecipient', e.target.checked)} className="rounded" />
              <span className="text-sm">Show recipient block</span>
            </label>
            {h.showRecipient && (
              <div className="space-y-2 pl-6">
                <Field label="Recipient label (e.g. Bill To)">
                  <input value={h.recipientLabel} onChange={e => set('recipientLabel', e.target.value)} className="field" />
                </Field>
                <Field label="Payload variable">
                  <input value={h.recipientVar} onChange={e => set('recipientVar', e.target.value)}
                    placeholder="recipient" className="field font-mono text-xs" />
                </Field>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// ── Section editor ────────────────────────────────────────────────────────────

function SectionEditor({ section, onChange, onRemove }: {
  section: PdfSection
  onChange: (s: PdfSection) => void
  onRemove: () => void
}) {
  const [open, setOpen] = useState(false)
  const titleMap: Record<string, string> = {
    text: (section as any).label || (section as any).variable || 'Text',
    table: (section as any).label || 'Table',
    summary: (section as any).label || 'Summary',
    signature: 'Signature',
    divider: 'Divider',
    spacer: `Spacer (${(section as any).height ?? 16}px)`,
  }

  return (
    <div className="border border-paper-border rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-paper-sunken cursor-pointer" onClick={() => setOpen(o => !o)}>
        <GripVertical size={13} className="text-ink-dim shrink-0" />
        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border shrink-0 ${
          section.type === 'table' ? 'bg-blue-50 text-blue-600 border-blue-100' :
          section.type === 'summary' ? 'bg-amber-50 text-amber-600 border-amber-100' :
          section.type === 'signature' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
          'bg-paper-sunken text-ink-dim border-paper-border'}`}>
          {SECTION_TYPE_LABELS[section.type] ?? section.type}
        </span>
        <span className="text-sm text-ink truncate flex-1">{titleMap[section.type]}</span>
        <button onClick={e => { e.stopPropagation(); onRemove() }} className="text-ink-dim hover:text-red-500 ml-1 shrink-0">
          <Trash2 size={12} />
        </button>
        <span className="text-ink-dim text-xs shrink-0">{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div className="p-3 space-y-3 border-t border-paper-border">
          <SectionForm section={section} onChange={onChange} />
        </div>
      )}
    </div>
  )
}

function SectionForm({ section, onChange }: { section: PdfSection; onChange: (s: PdfSection) => void }) {
  const set = (key: string, val: any) => onChange({ ...section, [key]: val } as any)

  if (section.type === 'text') return (
    <div className="space-y-2">
      <Field label="Label (shown above content)">
        <input value={(section as any).label} onChange={e => set('label', e.target.value)} className="field" />
      </Field>
      <Field label="Payload variable">
        <input value={(section as any).variable} onChange={e => set('variable', e.target.value)} className="field font-mono text-xs" />
      </Field>
      <div className="flex gap-4">
        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input type="checkbox" checked={(section as any).multiline} onChange={e => set('multiline', e.target.checked)} className="rounded" />
          Multi-line
        </label>
        <Field label="Style">
          <Select value={(section as any).style} onValueChange={v => set('style', v)}>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="bold">Bold</SelectItem>
            <SelectItem value="heading">Heading</SelectItem>
          </Select>
        </Field>
      </div>
    </div>
  )

  if (section.type === 'table') {
    const s = section as any
    const updateCol = (i: number, k: keyof PdfTableColumn, v: any) => {
      const cols = [...s.columns]; cols[i] = { ...cols[i], [k]: v }; set('columns', cols)
    }
    const addCol = () => set('columns', [...s.columns, { key: 'key', label: 'Column', width: 20, align: 'left', format: 'text' }])
    const removeCol = (i: number) => set('columns', s.columns.filter((_: any, j: number) => j !== i))
    return (
      <div className="space-y-3">
        <Field label="Label"><input value={s.label} onChange={e => set('label', e.target.value)} className="field" /></Field>
        <Field label="Payload array variable"><input value={s.variable} onChange={e => set('variable', e.target.value)} className="field font-mono text-xs" /></Field>
        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input type="checkbox" checked={s.stripe} onChange={e => set('stripe', e.target.checked)} className="rounded" /> Striped rows
        </label>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-ink-dim">Columns</span>
            <button onClick={addCol} className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover"><Plus size={11} /> Add</button>
          </div>
          <div className="space-y-1.5">
            {s.columns.map((col: PdfTableColumn, i: number) => (
              <div key={i} className="grid grid-cols-5 gap-1 items-center text-xs">
                <input value={col.key} onChange={e => updateCol(i, 'key', e.target.value)} placeholder="key" className="field col-span-1 font-mono !text-theme-xs" />
                <input value={col.label} onChange={e => updateCol(i, 'label', e.target.value)} placeholder="Label" className="field col-span-1 !text-theme-xs" />
                <input type="number" value={col.width} onChange={e => updateCol(i, 'width', +e.target.value)} placeholder="%" className="field col-span-1 !text-theme-xs" />
                <select value={col.format} onChange={e => updateCol(i, 'format', e.target.value)} className="field col-span-1 !text-theme-xs">
                  <option value="text">text</option><option value="number">number</option><option value="currency">currency</option><option value="date">date</option>
                </select>
                <button onClick={() => removeCol(i)} className="text-ink-dim hover:text-red-500 col-span-1 justify-self-center"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (section.type === 'summary') {
    const s = section as any
    const updateRow = (i: number, k: keyof PdfSummaryRow, v: any) => {
      const rows = [...s.rows]; rows[i] = { ...rows[i], [k]: v }; set('rows', rows)
    }
    const addRow = () => set('rows', [...s.rows, { label: 'Label', variable: 'variable', bold: false, separator: false, format: 'text' }])
    const removeRow = (i: number) => set('rows', s.rows.filter((_: any, j: number) => j !== i))
    return (
      <div className="space-y-3">
        <Field label="Label (optional)"><input value={s.label ?? ''} onChange={e => set('label', e.target.value)} className="field" /></Field>
        <Field label="Align">
          <Select value={s.align} onValueChange={v => set('align', v)}>
            <SelectItem value="left">Left</SelectItem>
            <SelectItem value="right">Right</SelectItem>
          </Select>
        </Field>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-ink-dim">Rows</span>
            <button onClick={addRow} className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover"><Plus size={11} /> Add row</button>
          </div>
          <div className="space-y-1.5">
            {s.rows.map((row: PdfSummaryRow, i: number) => (
              <div key={i} className="grid grid-cols-6 gap-1 items-center text-xs">
                <input value={row.label}    onChange={e => updateRow(i, 'label',    e.target.value)} placeholder="Label"    className="field col-span-2 !text-theme-xs" />
                <input value={row.variable} onChange={e => updateRow(i, 'variable', e.target.value)} placeholder="payload"  className="field col-span-1 font-mono !text-theme-xs" />
                <select value={row.format}  onChange={e => updateRow(i, 'format',   e.target.value)} className="field col-span-1 !text-theme-xs">
                  <option value="text">text</option><option value="number">number</option><option value="currency">$</option>
                </select>
                <label className="flex items-center gap-1 cursor-pointer col-span-1 text-theme-xs">
                  <input type="checkbox" checked={row.bold} onChange={e => updateRow(i, 'bold', e.target.checked)} className="rounded" /> Bold
                </label>
                <button onClick={() => removeRow(i)} className="text-ink-dim hover:text-red-500 col-span-1 justify-self-center"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (section.type === 'signature') {
    const s = section as any
    const updateBlock = (i: number, k: keyof PdfSignatureBlock, v: string) => {
      const blocks = [...s.blocks]; blocks[i] = { ...blocks[i], [k]: v }; set('blocks', blocks)
    }
    const addBlock = () => set('blocks', [...s.blocks, { label: 'Signature', nameVar: '', dateVar: '' }])
    const removeBlock = (i: number) => set('blocks', s.blocks.filter((_: any, j: number) => j !== i))
    return (
      <div className="space-y-3">
        <Field label="Label (optional)"><input value={s.label ?? ''} onChange={e => set('label', e.target.value)} className="field" /></Field>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-ink-dim">Signature blocks</span>
            <button onClick={addBlock} className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover"><Plus size={11} /> Add</button>
          </div>
          <div className="space-y-2">
            {s.blocks.map((b: PdfSignatureBlock, i: number) => (
              <div key={i} className="space-y-1.5 border border-paper-border rounded p-2">
                <div className="flex items-center gap-1 justify-between">
                  <span className="text-theme-xs text-ink-dim">Block {i + 1}</span>
                  <button onClick={() => removeBlock(i)} className="text-ink-dim hover:text-red-500"><Trash2 size={11} /></button>
                </div>
                <input value={b.label} onChange={e => updateBlock(i, 'label', e.target.value)} placeholder="Label (e.g. Client)" className="field !text-xs" />
                <div className="grid grid-cols-2 gap-1">
                  <input value={b.nameVar ?? ''} onChange={e => updateBlock(i, 'nameVar', e.target.value)} placeholder="nameVar" className="field font-mono !text-theme-xs" />
                  <input value={b.dateVar ?? ''} onChange={e => updateBlock(i, 'dateVar', e.target.value)} placeholder="dateVar" className="field font-mono !text-theme-xs" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (section.type === 'spacer') return (
    <Field label="Height (px)">
      <input type="number" value={(section as any).height} onChange={e => set('height', +e.target.value)} min="4" max="200" className="field w-24" />
    </Field>
  )

  return <p className="text-xs text-ink-dim italic">No options for this section type.</p>
}

// ── Footer tab ────────────────────────────────────────────────────────────────

function FooterPanel({ config, onChange }: { config: PdfTemplateConfig; onChange: (c: PdfTemplateConfig) => void }) {
  const f   = config.footer
  const set = (key: string, val: any) => onChange({ ...config, footer: { ...f, [key]: val } })
  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={f.enabled} onChange={e => set('enabled', e.target.checked)} className="rounded" />
        <span className="text-sm">Show footer</span>
      </label>
      {f.enabled && (
        <>
          <Field label="Footer text">
            <input value={f.text} onChange={e => set('text', e.target.value)} placeholder="Thank you for your business!" className="field" />
          </Field>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={f.showPageNumbers} onChange={e => set('showPageNumbers', e.target.checked)} className="rounded" />
            <span className="text-sm">Show page numbers</span>
          </label>
        </>
      )}
    </div>
  )
}

// ── Page tab ──────────────────────────────────────────────────────────────────

function PagePanel({ config, onChange }: { config: PdfTemplateConfig; onChange: (c: PdfTemplateConfig) => void }) {
  const set = (key: keyof PdfTemplateConfig, val: any) => onChange({ ...config, [key]: val })
  const setMargin = (k: string, v: number) => onChange({ ...config, margins: { ...config.margins, [k]: v } })
  return (
    <div className="space-y-4">
      <Field label="Page size">
        <Select value={config.pageSize} onValueChange={v => set('pageSize', v as any)}>
          <SelectItem value="A4">A4</SelectItem>
          <SelectItem value="Letter">Letter</SelectItem>
          <SelectItem value="Legal">Legal</SelectItem>
        </Select>
      </Field>
      <Field label="Orientation">
        <Select value={config.orientation} onValueChange={v => set('orientation', v as any)}>
          <SelectItem value="portrait">Portrait</SelectItem>
          <SelectItem value="landscape">Landscape</SelectItem>
        </Select>
      </Field>
      <div>
        <div className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest mb-2">Margins (mm)</div>
        <div className="grid grid-cols-2 gap-2">
          {(['top', 'right', 'bottom', 'left'] as const).map(side => (
            <Field key={side} label={side.charAt(0).toUpperCase() + side.slice(1)}>
              <input type="number" value={config.margins[side]} onChange={e => setMargin(side, +e.target.value)} min="0" max="50" className="field" />
            </Field>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Sections tab ──────────────────────────────────────────────────────────────

function SectionsPanel({ config, onChange }: { config: PdfTemplateConfig; onChange: (c: PdfTemplateConfig) => void }) {
  const [adding, setAdding] = useState(false)
  const [newType, setNewType] = useState<typeof SECTION_TYPES[number]>('text')

  const updateSection = useCallback((idx: number, s: PdfSection) => {
    const next = [...config.sections]; next[idx] = s
    onChange({ ...config, sections: next })
  }, [config, onChange])

  const removeSection = useCallback((idx: number) => {
    onChange({ ...config, sections: config.sections.filter((_, i) => i !== idx) })
  }, [config, onChange])

  const addSection = () => {
    const base = { id: uuidv4() }
    let section: PdfSection
    switch (newType) {
      case 'text':      section = { ...base, type: 'text',      label: '',   variable: '', multiline: false, style: 'normal' }; break
      case 'table':     section = { ...base, type: 'table',     label: '',   variable: 'items', columns: [], stripe: true }; break
      case 'summary':   section = { ...base, type: 'summary',   label: '',   align: 'right', rows: [] }; break
      case 'signature': section = { ...base, type: 'signature', label: '',   blocks: [{ label: 'Signed by', nameVar: '', dateVar: '' }] }; break
      case 'divider':   section = { ...base, type: 'divider' }; break
      case 'spacer':    section = { ...base, type: 'spacer',    height: 16 }; break
    }
    onChange({ ...config, sections: [...config.sections, section] })
    setAdding(false)
  }

  return (
    <div className="space-y-2">
      {config.sections.length === 0 && (
        <p className="text-xs text-ink-dim italic py-2">No sections yet. Add sections to define the document body.</p>
      )}
      {config.sections.map((s, i) => (
        <SectionEditor key={s.id} section={s}
          onChange={updated => updateSection(i, updated)}
          onRemove={() => removeSection(i)} />
      ))}
      {adding ? (
        <div className="flex items-center gap-2 pt-1">
          <Select value={newType} onValueChange={v => setNewType(v as any)} className="flex-1">
            {SECTION_TYPES.map(t => <SelectItem key={t} value={t}>{SECTION_TYPE_LABELS[t]}</SelectItem>)}
          </Select>
          <Button size="sm" onClick={addSection}>Add</Button>
          <Button size="sm" variant="secondary" onClick={() => setAdding(false)}>Cancel</Button>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover mt-2">
          <Plus size={12} /> Add section
        </button>
      )}
    </div>
  )
}

// ── Sample payload builder ────────────────────────────────────────────────────

function buildSamplePayload(config: PdfTemplateConfig): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  const today = new Date().toISOString().split('T')[0]

  // Header fields
  for (const f of config.header.fields) {
    if (f.variable) out[f.variable] = `Sample ${f.label}`
  }
  if (config.header.showRecipient && config.header.recipientVar) {
    out[config.header.recipientVar] = 'Jane Doe\n123 Main Street\nNew York, NY 10001'
  }

  // Sections
  for (const s of config.sections) {
    if (s.type === 'text') {
      if (s.variable) out[s.variable] = s.multiline ? 'Sample text content.\nSpanning multiple lines.' : 'Sample text content.'
    } else if (s.type === 'table') {
      if (s.variable) {
        const makeRow = () => {
          const row: Record<string, unknown> = {}
          for (const col of s.columns) {
            if (col.format === 'currency' || col.format === 'number') row[col.key] = 100
            else if (col.format === 'date') row[col.key] = today
            else row[col.key] = `Sample ${col.label}`
          }
          return row
        }
        out[s.variable] = [makeRow(), makeRow()]
      }
    } else if (s.type === 'summary') {
      for (const row of s.rows) {
        if (row.variable && !row.separator) {
          out[row.variable] = (row.format === 'currency' || row.format === 'number') ? 1234.56 : 'Sample value'
        }
      }
    } else if (s.type === 'signature') {
      for (const b of s.blocks) {
        if (b.nameVar) out[b.nameVar] = 'John Doe'
        if (b.dateVar) out[b.dateVar] = today
      }
    }
  }

  return out
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function PdfTemplateDetail() {
  const { id }    = useParams<{ id: string }>()
  const navigate  = useNavigate()
  const qc        = useQueryClient()
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const { data: template, isLoading } = useQuery({
    queryKey: ['pdf-template', id],
    queryFn:  () => getPdfTemplate(id!).then(r => r.data),
    enabled:  !!id,
  })

  const [config, setConfig]           = useState<PdfTemplateConfig | null>(null)
  const [name, setName]               = useState('')
  const [description, setDescription] = useState('')
  const [htmlOverride, setHtmlOverride] = useState<string | null>(null)
  const [configTab, setConfigTab]     = useState<ConfigTab>('branding')
  const [htmlMode, setHtmlMode]       = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [showGenerate, setShowGenerate]     = useState(false)
  const [generatePayload, setGeneratePayload] = useState('{}')
  const [generateMode, setGenerateMode] = useState<'sync' | 'async'>('sync')
  const [generateResult, setGenerateResult] = useState<{ documentUrl?: string; jobId?: string } | null>(null)

  // Sync local state when template loads
  useEffect(() => {
    if (template && !config) {
      setConfig(template.config)
      setName(template.name)
      setDescription(template.description ?? '')
      setHtmlOverride(template.htmlOverride ?? null)
    }
  }, [template, config])

  const saveMut = useMutation({
    mutationFn: () => updatePdfTemplate(id!, {
      name,
      description: description || undefined,
      config: config!,
      htmlOverride: htmlOverride ?? null,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pdf-template', id] }),
  })

  const generateMut = useMutation({
    mutationFn: () => {
      let payload: Record<string, unknown> = {}
      try { payload = JSON.parse(generatePayload) } catch { /* use empty */ }
      return generatePdf({ templateId: id!, payload, mode: generateMode })
    },
    onSuccess: (res) => setGenerateResult({ documentUrl: res.data.documentUrl, jobId: res.data.jobId }),
  })

  const refreshPreview = async () => {
    if (!id) return
    setPreviewLoading(true)
    try {
      // Save first so the preview reflects current state
      if (!template?.isSystem) await saveMut.mutateAsync()
      const res = await previewPdfTemplate(id)
      if (iframeRef.current) {
        iframeRef.current.srcdoc = typeof res.data === 'string' ? res.data : String(res.data)
      }
    } catch { /* ignore */ }
    setPreviewLoading(false)
  }

  const enableHtmlOverride = () => {
    // Show current generated HTML in editor so the user can start from it
    setHtmlMode(true)
    if (!htmlOverride) {
      // We'll show a placeholder note; the actual HTML is server-generated
      setHtmlOverride(`<!-- Override HTML replaces the config-generated output.
     Use {{variable}} for Handlebars interpolation, {{#each array}}…{{/each}} for loops. -->
`)
    }
  }

  if (isLoading || !config) return <div className="py-16 text-center text-sm text-ink-dim">Loading…</div>
  if (!template) return <div className="py-16 text-center text-sm text-red-500">Template not found</div>

  const readOnly = template.isSystem

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb + header */}
      <div className="flex items-center gap-1.5 mb-3 text-sm">
        <button onClick={() => navigate('/pdf-templates')} className="text-ink-dim hover:text-ink transition-colors">
          PDF Templates
        </button>
        <ChevronRight size={13} className="text-ink-dim" />
        <span className="text-ink font-medium">{template.name}</span>
        {readOnly && (
          <span className="ml-1 text-[10px] font-medium px-1.5 py-px rounded border bg-paper-sunken text-ink-dim border-paper-border">system</span>
        )}
      </div>

      {readOnly && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-700">
          This is a read-only system template.
          <button onClick={() => navigate(-1)} className="underline ml-1">Clone it</button> to create a customizable copy.
        </div>
      )}

      {!readOnly && (
        <div className="flex items-center gap-3 mb-4">
          <input value={name} onChange={e => setName(e.target.value)}
            className="field font-semibold text-base flex-1 max-w-xs" placeholder="Template name" />
          <input value={description} onChange={e => setDescription(e.target.value)}
            className="field text-sm flex-1 max-w-sm" placeholder="Description (optional)" />
          <Button icon={Save} onClick={() => saveMut.mutate()} loading={saveMut.isPending} className="shrink-0">
            Save
          </Button>
          <Button
            variant="secondary"
            icon={Download}
            onClick={() => { setGeneratePayload(JSON.stringify(buildSamplePayload(config), null, 2)); setShowGenerate(true) }}
            className="shrink-0"
          >
            Generate PDF
          </Button>
        </div>
      )}

      {/* Split view */}
      <div className="flex gap-4 flex-1 min-h-0">
        {/* Left: config panel */}
        {!readOnly && (
          <div className="w-2/5 shrink-0 flex flex-col border border-paper-border rounded-lg overflow-hidden">
            {/* Tab bar */}
            <div className="flex border-b border-paper-border bg-paper-sunken overflow-x-auto">
              {(['branding', 'header', 'sections', 'footer', 'page'] as ConfigTab[]).map(t => (
                <button key={t} onClick={() => setConfigTab(t)}
                  className={`px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                    configTab === t ? 'border-accent text-accent' : 'border-transparent text-gray-500 hover:text-gray-700-dim hover:text-ink'}`}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
            {/* Panel content */}
            <div className="flex-1 overflow-y-auto p-4">
              {configTab === 'branding'  && <BrandingPanel  config={config} onChange={setConfig} />}
              {configTab === 'header'    && <HeaderPanel    config={config} onChange={setConfig} />}
              {configTab === 'sections'  && <SectionsPanel  config={config} onChange={setConfig} />}
              {configTab === 'footer'    && <FooterPanel    config={config} onChange={setConfig} />}
              {configTab === 'page'      && <PagePanel      config={config} onChange={setConfig} />}
            </div>
          </div>
        )}

        {/* Right: preview */}
        <div className="flex-1 flex flex-col border border-paper-border rounded-lg overflow-hidden min-w-0">
          {/* Preview toolbar */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-paper-border bg-paper-sunken shrink-0">
            <div className="flex items-center gap-1 text-theme-xs text-ink-dim mr-auto">
              <Eye size={12} /> Preview
            </div>
            {!readOnly && (
              <>
                <button
                  onClick={() => { setHtmlMode(false); setHtmlOverride(null) }}
                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${!htmlMode ? 'bg-paper-sunken text-ink' : 'text-ink-dim hover:text-ink'}`}>
                  Config
                </button>
                <button
                  onClick={enableHtmlOverride}
                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${htmlMode ? 'bg-paper-sunken text-ink' : 'text-ink-dim hover:text-ink'}`}>
                  <Code size={11} /> HTML override
                </button>
              </>
            )}
            <button onClick={refreshPreview} disabled={previewLoading}
              className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover disabled:opacity-50 ml-1">
              <RefreshCw size={11} className={previewLoading ? 'animate-spin' : ''} />
              {previewLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {htmlMode && !readOnly ? (
            <div className="flex-1 flex flex-col">
              <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-700">
                HTML override active — supports Handlebars: <code className="bg-amber-100 px-1 rounded">{"{{variable}}"}</code>, <code className="bg-amber-100 px-1 rounded">{"{{#each items}}…{{/each}}"}</code>
              </div>
              <textarea
                value={htmlOverride ?? ''}
                onChange={e => setHtmlOverride(e.target.value)}
                className="flex-1 font-mono text-xs p-3 resize-none focus:outline-none bg-[#1e1e1e] text-[#d4d4d4] leading-relaxed"
                spellCheck={false}
              />
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              className="flex-1 w-full"
              title="PDF preview"
              sandbox="allow-same-origin"
              srcDoc="<div style='font-family:Arial;color:#aaa;padding:32px;text-align:center;'>Click <b>Refresh</b> to preview the document</div>"
            />
          )}
        </div>
      </div>

      {/* Generate PDF modal */}
      {showGenerate && (
        <Modal title="Generate PDF" onClose={() => { setShowGenerate(false); setGenerateResult(null) }} size="sm">
          {generateResult ? (
            <div className="space-y-4 text-center py-2">
              <div className="text-emerald-600 font-medium">PDF generated successfully!</div>
              {generateResult.documentUrl && (
                <a href={generateResult.documentUrl} target="_blank" rel="noreferrer"
                  className="btn-primary inline-flex">
                  <Download size={13} /> Download PDF
                </a>
              )}
              {generateResult.jobId && !generateResult.documentUrl && (
                <p className="text-sm text-ink-dim">Async job queued — ID: <code className="bg-paper-sunken px-1 rounded text-xs">{generateResult.jobId}</code></p>
              )}
              <Button variant="secondary" onClick={() => { setShowGenerate(false); setGenerateResult(null) }}>Close</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <Field label="Mode">
                <Select value={generateMode} onValueChange={v => setGenerateMode(v as any)}>
                  <SelectItem value="sync">Sync (wait for result)</SelectItem>
                  <SelectItem value="async">Async (queue job + buzz event)</SelectItem>
                </Select>
              </Field>
              <Field label="Payload (JSON)">
                <textarea
                  value={generatePayload}
                  onChange={e => setGeneratePayload(e.target.value)}
                  rows={8}
                  className="field font-mono text-xs resize-none"
                  placeholder='{"invoiceNumber": "INV-001", "date": "2024-01-01", ...}'
                />
              </Field>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={() => setShowGenerate(false)}>Cancel</Button>
                <Button onClick={() => generateMut.mutate()} loading={generateMut.isPending}>
                  Generate
                </Button>
              </div>
              {generateMut.isError && (
                <p className="text-xs text-red-600">Generation failed. Check the payload and try again.</p>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  )
}
